import React, { useState, useRef, useEffect, useContext } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import { Checkin, Feature, ManualOverrideType } from '../api/client'
import type { ParseActivity } from '../hooks/useParseActivity'
import CheckinComposer, { ComposerCheckin } from './CheckinComposer'
import ParseActivityPanel, { hasParseDetail } from './checkin/ParseActivityPanel'
import ReferenceValueDisplay from './ReferenceValueDisplay'
import { ClickCard } from './ClickCard'
import { CollectionItemHoverCard } from './CollectionItemHoverCard'
import ListValueCard from './ListValueCard'
import ListValueDisplay from './ListValueDisplay'
import MapValueDisplay from './MapValueDisplay'
import LocationChips, { hasLocationChips } from './LocationChips'
import CheckinLocationsMap, { hasCheckinLocationsMap } from './CheckinLocationsMap'
import CheckinExpandedMapModal from './checkin/CheckinExpandedMapModal'
import LocationDetailModal, { LocationDetailValue } from './checkin/LocationDetailModal'
import ParseFeedbackPopover from './checkin/ParseFeedbackPopover'
import PersonChips, { hasPersonChips } from './PersonChips'
import FeatureLockBadge from './FeatureLockBadge'
import PersonDetailModal, { PersonDetailValue } from './checkin/PersonDetailModal'
import MapValueCard, { MapRefTap } from './MapValueCard'
import CollectionItemModal from './collections/CollectionItemModal'
import EditCollectionItemById from './collections/EditCollectionItemById'
import { hasActiveCheckinFeatureRow, ownerOf } from '../utils/checkinFeatures'
import { boolLabelFor } from '../utils/boolFeature'
import { formatCheckinTime, formatClock } from '../utils/dates'
import {
    TIMESTAMP_DISPLAY_LABELS,
    nextTimestampDisplay,
    useTimestampDisplay,
} from '../hooks/useTimestampDisplay'
import { UserContext } from './UserContext'

const NEW_CHECKIN_ID_PLACEHOLDER = -1

interface CheckinItemProps {
    checkin: Checkin
    index: number
    showSeparator: boolean
    editingCheckinId: number | null
    showNewCheckinComposer: boolean
    features: Feature[]
    recentFeatures: Feature[]
    sortOrder: string
    savingState: 'unsaved' | 'saving' | 'ai-parsing' | null
    /** Auto-parse countdown for this checkin, forwarded to the composer. */
    parseCountdown?: { armedAt: number; dueAt: number } | null
    onParseNow?: () => void
    /** Close the composer. Given by the page so closing can also take over a
     *  parse still counting down; without it, closing just stops editing. */
    onComposerClose?: () => void
    /** The live record of this checkin's parse, when one is running. Replaces
     *  the old `isAnalyzing` boolean: the indicator is the same one, but it now
     *  carries what the parse is doing. Absent means no indicator, which is what
     *  every read-only list passes. */
    activity?: ParseActivity
    /** Expand / collapse the parse panel. Absent leaves it non-expandable. */
    onToggleParseExpanded?: (expanded: boolean) => void
    onToggleParseTechnical?: (technical: boolean) => void
    /** Read this check-in's last parse back out of the store and show it on the
     *  row. Absent — every read-only list — leaves the menu entry out. */
    onReviewParse?: (id: number) => void
    showMenu?: boolean // New prop to control menu visibility
    /** When true (default), renders an inline mini-map below the chips for
     *  checkins whose locations have coords. Pass false from contexts that
     *  already render a map of the same place(s) — e.g. LocationDetailModal
     *  showing recent checkins for a single place. */
    showMap?: boolean
    availableActions?: Array<'edit' | 'duplicate' | 'delete' | 'reanalyze' | 'edit-in-page' | 'discuss' | 'save-as-template'>
    onDiscuss?: (id: number) => void
    /** Snapshot this checkin as a one-tap template. Required for the
     *  'save-as-template' action to render. */
    onSaveAsTemplate?: (id: number) => void
    isSelectionMode?: boolean
    isSelected?: boolean
    onToggleSelection?: (id: number) => void
    /** The grab handle for drag-to-reorder, built by whoever owns the drag —
     *  it has to ride the element the user actually grabs, so the listeners
     *  come in already attached. Absent on every surface that isn't the
     *  timeline, which is most of them. Rendered in the header's right-hand
     *  cluster beside the actions menu, for the same reason the dashboard's
     *  summary cards put theirs there: the card is full of interactive parts
     *  and arming the whole of it would fight all of them. */
    dragHandle?: React.ReactNode
    /** A time to flash on this row and then let fade — the feedback that a
     *  drag took effect. Shown as a clock in every display mode, including
     *  `none`, and it is the *move* that is being reported, not the setting;
     *  when it clears, the row falls back to whatever the mode renders, which
     *  under `none` is nothing. */
    timeFlash?: string | null
    spineClass?: string // Custom spine styling
    collectionItems: Record<string, any> // Collection items data for reference resolution
    /** Replaces the body paragraph. The Genie chat clips the body to a few lines
     *  and hangs a "Read more" modal off it; the timeline renders it in full. */
    renderBody?: (body: string) => React.ReactNode
    formatDate: (dateString: string, includeYearIfDifferent: boolean) => string
    getComposerCheckinFromCheckin: (checkin: Checkin) => ComposerCheckin
    handleFieldEdit: (field: string, value: any) => void
    handleDeleteCheckin: (id: number) => void
    handleEditCheckin: (checkin: Checkin) => void
    handleDuplicateCheckin: (id: number) => void
    handleFilterSelect: (type: 'feature' | 'project', item: Feature | any) => void
    handleReanalyzeCheckin: (id: number) => void
    onRefresh?: () => void
    /** Forwarded to the inline `CheckinComposer` so the location picker
     *  can hand newly-created Places to the page-level cache. */
    onCollectionItemReady?: (item: { id: number; data: Record<string, any> }) => void
    setEditingCheckinId: (id: number | null) => void
    setCheckins: (updater: (prev: Checkin[]) => Checkin[]) => void
    setShowNewCheckinComposer: (show: boolean) => void
    firstElementIsEmptyCheckin: (checkins: Checkin[]) => boolean
}

const CheckinItem: React.FC<CheckinItemProps> = ({
    checkin,
    index,
    showSeparator,
    editingCheckinId,
    showNewCheckinComposer,
    features,
    recentFeatures,
    sortOrder,
    savingState,
    parseCountdown,
    onParseNow,
    onComposerClose,
    activity,
    onToggleParseExpanded,
    onToggleParseTechnical,
    onReviewParse,
    showMenu: showMenuProp = true, // Default to true for backward compatibility
    showMap = true,
    availableActions = ['edit', 'duplicate', 'delete', 'reanalyze'],
    onDiscuss,
    onSaveAsTemplate,
    isSelectionMode = false,
    isSelected = false,
    onToggleSelection,
    dragHandle,
    timeFlash,
    spineClass,
    collectionItems = {},
    renderBody,
    formatDate,
    getComposerCheckinFromCheckin,
    handleFieldEdit,
    handleDeleteCheckin,
    handleEditCheckin,
    handleDuplicateCheckin,
    handleFilterSelect,
    handleReanalyzeCheckin,
    onRefresh,
    onCollectionItemReady,
    setEditingCheckinId,
    setCheckins,
    setShowNewCheckinComposer,
    firstElementIsEmptyCheckin
}) => {
    const [showMenu, setShowMenu] = useState(false)
    const [showParseFeedback, setShowParseFeedback] = useState(false)
    // Read off the user rather than taken as a prop: CheckinItem is rendered
    // from the timeline, the Genie chat, the location and person modals and
    // the set view, and the setting is the same one everywhere. `null` while
    // the user is still loading, and in tests, which lands on `none` — the
    // default, so nothing appears and nothing moves.
    const { user } = useContext(UserContext)
    // Read *and written* from here: the date label is one of the setting's
    // three entry points, and the one that teaches the gesture. The hook is
    // what keeps a tap here, the sort menu and the account page writing the
    // same column and updating the same cached user object.
    const { mode: timestampMode, cycle: cycleTimestampDisplay } = useTimestampDisplay()
    const timeLabel = formatCheckinTime(checkin.applies_at, user?.timestampDisplay, checkin.applies_precision)
    // What sits beside the date right now. A flash outranks the mode's own
    // label for as long as it lasts: the drag just gave this checkin a time
    // and saying so is the point, whereas `approximate` would answer with a
    // word the user did not aim for and `none` with nothing at all.
    const flashLabel = timeFlash ? formatClock(timeFlash) : null
    const shownTimeLabel = flashLabel ?? timeLabel
    const isSavedCheckin = checkin.id !== NEW_CHECKIN_ID_PLACEHOLDER
    // Whether this row is being edited rather than read. One expression, used
    // by both branches below, because they are each other's negation and a
    // second copy of it is a second thing to keep in step.
    const composerOpen = (checkin.id === NEW_CHECKIN_ID_PLACEHOLDER
        && editingCheckinId === null
        && showNewCheckinComposer)
        || editingCheckinId === checkin.id
    // ...and whether it is *still leaving*. The composer's exit plays after
    // `editingCheckinId` clears, so for those few hundred milliseconds the
    // composer and the read view are both on screen — and since stage 10 both
    // of them mount a `ParseActivityPanel`, closing a composer mid-parse put
    // two indicators for one check-in on the page at once, the exiting one out
    // of layout flow under `mode="popLayout"` and therefore in the wrong place
    // (reported 2026-09-18). The invariant is "one panel per check-in", and it
    // has to hold *during* the transition, not only at rest.
    //
    // The read view gives way rather than the composer: the panel the reader
    // is watching is the one already on screen, and taking it off mid-fade to
    // re-mount an identical one a pixel away is the swap this is avoiding.
    // Nothing about the composer's own exit changes.
    const [composerExiting, setComposerExiting] = useState(false)
    // Adjusted during render rather than in an effect, so no commit ever
    // contains the state this exists to prevent: an effect would run after the
    // frame in which both panels were mounted, which is the frame the bug is.
    const wasComposerOpen = useRef(composerOpen)
    if (wasComposerOpen.current !== composerOpen) {
        wasComposerOpen.current = composerOpen
        setComposerExiting(!composerOpen)
    }
    // Whether the parse panel is actually showing rows, which is what the
    // header row's alignment turns on. Not just `activity.expanded`: the panel
    // hides its own chevron until the parse has announced a step, so an
    // `expanded` record with nothing in it still renders the plain indicator —
    // and while the composer is leaving, the read view is not rendering the
    // panel at all, so the row should sit the way it does without one.
    const parseShowingRows = Boolean(
        activity?.expanded
            && onToggleParseExpanded
            && hasParseDetail(activity)
            && !composerExiting,
    )
    // ...but the alignment must not follow that flag down on its own schedule.
    // Collapsing the drawer, or closing the analysis outright, flips it in the
    // frame the click lands while the rows are still on screen fading: the row
    // re-centres against a panel that is still tall, and the date and the
    // actions menu visibly drop below the indicator for the length of the exit
    // (reported 2026-09-20, reproduced in the browser). So the row stays
    // top-aligned until the rows are *gone*, not until they are told to go.
    const [parseRowsSettling, setParseRowsSettling] = useState(false)
    // Render-time adjustment, for the same reason `composerExiting` uses one:
    // an effect would land a frame late, and the frame it missed is the bug.
    const hadParseRows = useRef(parseShowingRows)
    if (hadParseRows.current !== parseShowingRows) {
        hadParseRows.current = parseShowingRows
        setParseRowsSettling(!parseShowingRows)
    }
    // The panel used to animate out from an `AnimatePresence` here, and its
    // `onExitComplete` was what released the hold when the analysis was closed
    // outright — the drawer's own `onRowsSettled` cannot fire, because the
    // component carrying it is gone. The panel's halves are grid items now and
    // that wrapper is gone with it, so the release moves here: no panel on the
    // card means nothing to stay top-aligned for. Without it the row stays
    // top-aligned for good after a "Show analysis" that gets closed again.
    if (!(activity && !composerExiting) && parseRowsSettling) {
        setParseRowsSettling(false)
    }
    const parseExpanded = parseShowingRows || parseRowsSettling
    // Currently-selected pin's row key. Tap a pin to set it; the next
    // mousedown anywhere (including a different pin or an outside click)
    // clears it via the effect below. Lives on CheckinItem because the
    // chips and the map are siblings, neither of which owns the other.
    const [highlightedLocationKey, setHighlightedLocationKey] = useState<string | null>(null)
    const [expandedMapOpen, setExpandedMapOpen] = useState(false)
    // The LocationDetailModal lives at this level (not inside LocationChips
    // anymore) so it can be opened from two distinct sources: a chip click,
    // and a pin tap inside the expanded-map modal. Keeping a single modal
    // instance avoids the two-modal stack the user explicitly didn't want.
    const [locationDetailValue, setLocationDetailValue] = useState<LocationDetailValue | null>(null)
    // Modals owned here so map-card reference taps and single-ref People taps
    // can both open the same surface without each chip wiring its own state.
    const [personDetailValue, setPersonDetailValue] = useState<PersonDetailValue | null>(null)
    const [genericRefValue, setGenericRefValue] = useState<MapRefTap | null>(null)
    // Edit hand-off target for the chip-tap viewer modals. When set, a
    // CollectionItemModal in edit mode mounts on top of the detail modal —
    // the detail modal stays open behind it so a Cancel returns the user
    // exactly where they were.
    const [editingCollectionItem, setEditingCollectionItem] = useState<
        { collectionId: number; itemId: number } | null
    >(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const navigate = useNavigate()


    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setShowMenu(false)
            }
        }

        if (showMenu) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showMenu])

    // Clear the pin highlight on the next mousedown anywhere. mousedown
    // fires before click in the standard event sequence, so when the user
    // taps a different pin: this clears the old key first, then the pin's
    // own onClick sets the new one. Tapping outside any pin: this clears
    // it and nothing re-sets. Listener is only attached while a key is
    // selected to avoid a global handler running for every checkin in the
    // timeline at all times.
    useEffect(() => {
        if (highlightedLocationKey == null) return
        const clear = () => setHighlightedLocationKey(null)
        document.addEventListener('mousedown', clear)
        return () => document.removeEventListener('mousedown', clear)
    }, [highlightedLocationKey])

    return (
        <div key={index} className="mb-4">
            {/* Separator above each checkin except the first - only on small screens where spine doesn't show */}
            {showSeparator && (
                <div className="flex sm:hidden justify-center mb-4">
                    <div className="w-8 h-px bg-gray-300"></div>
                </div>
            )}
            {/* Composer (covers both new-mode and edit-mode in one JSX
                position so React preserves the CheckinComposer instance
                across the placeholder→real-id save — textarea focus
                survives). AnimatePresence stays mounted alongside the
                read-only branch below so the composer's exit can play
                when editingCheckinId clears. The new-mode placeholder
                unmounts with its parent CheckinItem on cancel and so
                doesn't get an exit animation — acceptable since users
                normally save rather than cancel a new composer. */}
            <AnimatePresence
                mode="popLayout"
                onExitComplete={() => setComposerExiting(false)}
            >
                {composerOpen && (
                    <motion.div
                        key="composer"
                        initial={{ opacity: 0, scale: 0.97, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.97, y: -10 }}
                        transition={{ type: 'spring', stiffness: 380, damping: 28 }}
                    >
                        <CheckinComposer
                            editingCheckinId={checkin.id}
                            composerCheckin={getComposerCheckinFromCheckin(checkin)}
                            features={features}
                            recentFeatures={recentFeatures}
                            collectionItems={collectionItems}
                            onFieldEdited={handleFieldEdit}
                            onDelete={
                                editingCheckinId === checkin.id
                                    ? handleDeleteCheckin
                                    : undefined
                            }
                            onCancel={
                                editingCheckinId === checkin.id
                                    ? onComposerClose ?? (() => setEditingCheckinId(null))
                                    : undefined
                            }
                            onCollectionItemReady={onCollectionItemReady}
                            projectDetails={checkin.project_details}
                            savingState={savingState}
                            parseCountdown={parseCountdown}
                            onParseNow={onParseNow}
                            activity={activity}
                            onToggleParseExpanded={onToggleParseExpanded}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
            {/* The spine below is 2px of `action`, not the 4px amber-500 it
                was: on the blush ground a 4px full-strength gold was the
                heaviest ink in the card and read as a slab rather than a
                marker. Width is the lever that matters — lightening a 4px
                rail either stays a slab (amber-300) or disappears
                (amber-200) — and halving it also retires the last literal
                amber here in favor of the token. */}
            {!composerOpen && (
                <div className={spineClass || "sm:border-l-2 px-3 sm:pl-4 sm:pr-0 py-3 sm:border-action"}>
                    {/* `items-start` only while the parse panel is open: the
                        panel grows downward from the indicator, and an
                        `items-center` row would slide the date and the actions
                        menu to the middle of it. Collapsed, this is the row it
                        has always been. */}
                    <div className={`flex mb-2 ${parseExpanded ? 'items-start' : 'items-center'}`}>
                        {/* Selection checkbox */}
                        {isSelectionMode && (
                            <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => onToggleSelection?.(checkin.id)}
                                className="mr-3 h-4 w-4 text-amber-800 border-gray-300 rounded focus:ring-action"
                            />
                        )}
                        {/* A grid, not a flex row, once a panel is on the
                            card: the expanded box has to match the width of
                            the body below rather than being sized by its own
                            content, which left it ~240px wide and lined up
                            with nothing (reported 2026-09-22). Two columns —
                            the date, then everything else — and the box takes
                            `col-span-2` so it starts at the date's left edge
                            and ends at the row's right edge, which is exactly
                            the body's measure. `minmax(0,1fr)` rather than
                            `1fr` so the second column may shrink below its
                            content and the status line can still truncate.

                            On a phone there are **two** columns and the status
                            phrase takes a row of its own beneath the date
                            (reported 2026-09-22): "Sun, May 18" and a menu
                            leave well under half the width for a line like
                            "Checking for relationship candidates", so most of
                            the time it had nowhere to go. The date keeps the
                            menu company on the first row, which is what that
                            row is for. */}
                        <div
                            className={`grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[auto_minmax(0,1fr)_auto] gap-x-3 flex-1 min-w-0 ${
                                parseExpanded ? 'items-start' : 'items-center'
                            }`}
                        >
                            {/* The time of day, when the checkin has one and
                                the user asked to see it, sits inside the date's
                                span rather than beside it: this is a grid, and
                                a sibling element would become a fourth column
                                and reflow the row. With no time — every
                                pre-existing checkin — nothing is rendered at
                                all, so the row is byte-identical to what it
                                has always been. */}
                            <span className="font-medium text-gray-800 shrink-0">
                                {/* The date is a button: tapping it cycles the
                                    display mode for the whole timeline, which
                                    is the fast path to a setting that used to
                                    live only on the account page. The button is
                                    *inside* the span rather than replacing it,
                                    for the same reason the time is — the span
                                    is this grid's first column, and swapping
                                    what kind of box it is would be a layout
                                    change for a behaviour change.

                                    `select-none` and the webkit callout reset
                                    because a press on a phone otherwise starts
                                    the browser's own text selection instead of
                                    reading as a tap — the trap the reorder grip
                                    already fell into (55fc964). The label says
                                    what the button does; the date alone would
                                    read as a date. */}
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        void cycleTimestampDisplay()
                                    }}
                                    style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
                                    className="select-none rounded text-left hover:text-gray-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-action"
                                    title={`Show times: ${TIMESTAMP_DISPLAY_LABELS[timestampMode]}. Tap to change.`}
                                    aria-label={`Show times on the timeline — ${TIMESTAMP_DISPLAY_LABELS[timestampMode].toLowerCase()}. Tap to change to ${TIMESTAMP_DISPLAY_LABELS[nextTimestampDisplay(timestampMode)].toLowerCase()}.`}
                                >
                                    {formatDate(checkin.applies_to, sortOrder === 'most_relevant')}
                                    {/* One slot, two tenants. `mode="wait"` so a
                                        flash that fades to a different word — the
                                        clock giving way to `afternoon` under
                                        `approximate` — swaps rather than
                                        crossfading two labels on top of each
                                        other. Keyed on which tenant it is, so a
                                        flash arriving over a label that is already
                                        there still plays. */}
                                    <AnimatePresence mode="wait" initial={false}>
                                        {shownTimeLabel && (
                                            <motion.span
                                                key={flashLabel ? 'flash' : 'label'}
                                                data-testid={flashLabel ? 'checkin-time-flash' : 'checkin-time-label'}
                                                initial={{ opacity: 0, scale: 0.6 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.6 }}
                                                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                                style={{ transformOrigin: 'left center' }}
                                                className={`ml-2 inline-block ${
                                                    flashLabel
                                                        ? 'rounded bg-amber-100 px-1.5 font-medium text-amber-800'
                                                        : 'font-normal text-gray-500'
                                                }`}
                                            >
                                                {shownTimeLabel}
                                            </motion.span>
                                        )}
                                    </AnimatePresence>
                                </button>
                            </span>
                            {/* The read view's panel. The composer branch
                                above renders its own, on its own status line;
                                the two branches are mutually exclusive by the
                                guard on this one, which is what keeps a
                                check-in from ever showing two panels. */}
                            {/* Mounted and unmounted, so it animates: "Show
                                analysis" puts a finished parse on this row and
                                the chevron takes it away again, and both used
                                to happen in one frame (reported 2026-09-18).
                                The surface spring the drawer inside the panel
                                already uses, from the same origin — the panel
                                arrives the way the region behind its chevron
                                does, rather than in a second vocabulary. */}
                            {/* Closing the analysis outright unmounts the panel,
                                so the drawer's own `onRowsSettled` never fires
                                — the component carrying it is gone. Clear the
                                alignment hold here too, or the row stays
                                top-aligned for good after a "Show analysis"
                                that gets closed again. */}
                            {/* The reveal used to wrap the whole panel from
                                out here. It cannot any more: the panel's two
                                halves are now grid items in their own right,
                                and a wrapper around both would put them back
                                in one box. It would also have swept the date
                                into the animation, which is wrong — the date
                                was already on screen, and sliding it 8px every
                                time an analysis opens is a flicker, not a
                                reveal. The panel animates its own status line
                                in, and the expanded box has always animated
                                itself. */}
                            {activity && !composerExiting && (
                                <ParseActivityPanel
                                    inGrid
                                    activity={activity}
                                    onToggleExpanded={onToggleParseExpanded}
                                    onToggleTechnical={onToggleParseTechnical}
                                    onRowsSettled={() => setParseRowsSettling(false)}
                                />
                            )}
                        {/* The actions menu joins the grid as its third
                            column rather than sitting outside it. It shares
                            the row with the date and the status line, and the
                            expanded box spans all three — which is what makes
                            the box end where the body ends. Left outside, it
                            held back the row's right edge and the box came up
                            28px short of the text below it. */}
                        <div className="col-start-2 sm:col-start-3 row-start-1 flex items-center shrink-0">
                        {/* The reorder grip, when the surface offers one. It
                            keeps the actions menu company rather than sitting
                            on the body, so the whole card stays tappable and a
                            phone's scroll is never competing with a drag. */}
                        {dragHandle}
                        {/* Parse-feedback surface, opened from the actions
                            menu's "Report" entry; notes land in
                            parse_feedback_notes attributed to the latest
                            parse job. The empty anchor keeps the popover
                            hanging off the header's right edge. */}
                        {isSavedCheckin && (
                            <ParseFeedbackPopover
                                checkinId={checkin.id}
                                open={showParseFeedback}
                                onClose={() => setShowParseFeedback(false)}
                            />
                        )}
                        {showMenuProp && availableActions.length > 0 && (
                            <div className="relative" ref={menuRef}>
                                <button
                                    onClick={() => setShowMenu(!showMenu)}
                                    className="text-gray-500 hover:text-gray-700 cursor-pointer p-1"
                                    title="More actions"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                    </svg>
                                </button>
                            {showMenu && (
                                <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                                    {availableActions.includes('edit-in-page') && (
                                        <button
                                            onClick={() => {
                                                setShowMenu(false)
                                                navigate(`/checkins?checkin_id=${checkin.id}&edit=1`)
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                            Edit
                                        </button>
                                    )}
                                    {availableActions.includes('edit') && (
                                        <button
                                            onClick={() => {
                                                setCheckins(prev => {
                                                    if (firstElementIsEmptyCheckin(prev)) {
                                                        return prev.slice(1)
                                                    }
                                                    return prev
                                                })
                                                setShowNewCheckinComposer(false)
                                                handleEditCheckin(checkin)
                                                setShowMenu(false)
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                            </svg>
                                            Edit
                                        </button>
                                    )}
                                    {availableActions.includes('duplicate') && (
                                        <button
                                            onClick={() => {
                                                handleDuplicateCheckin(checkin.id)
                                                setShowMenu(false)
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                            Duplicate
                                        </button>
                                    )}
                                    {availableActions.includes('save-as-template') && onSaveAsTemplate && (
                                        <button
                                            onClick={() => {
                                                onSaveAsTemplate(checkin.id)
                                                setShowMenu(false)
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                            </svg>
                                            Save as shortcut
                                        </button>
                                    )}
                                    {availableActions.includes('delete') && (
                                        <button
                                            onClick={() => {
                                                handleDeleteCheckin(checkin.id)
                                                setShowMenu(false)
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center text-rose-600"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                            Delete
                                        </button>
                                    )}
                                    {/* Everything that isn't CRUD, under one separator. */}
                                    {(isSavedCheckin || availableActions.includes('reanalyze')
                                        || (availableActions.includes('discuss') && onDiscuss)) && (
                                        <div className="border-t border-gray-100">
                                        {/* Loads this check-in on its own, dropping whatever
                                            filters the timeline is currently under. */}
                                        {isSavedCheckin && (
                                            <button
                                                onClick={() => {
                                                    setShowMenu(false)
                                                    navigate(`/?checkin_id=${checkin.id}`)
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 3h6m0 0v6m0-6l-7 7M9 21H3m0 0v-6m0 6l7-7" />
                                                </svg>
                                                Focus
                                            </button>
                                        )}
                                        {availableActions.includes('reanalyze') && (
                                            <button
                                                onClick={() => {
                                                    handleReanalyzeCheckin(checkin.id)
                                                    setShowMenu(false)
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Analyze
                                            </button>
                                        )}
                                        {/* The way back into a finished parse. It
                                            sits next to "Analyze" on purpose: one
                                            re-runs the analysis, the other shows
                                            what the last one did. Gated on the
                                            handler rather than on `availableActions`
                                            so read-only lists, which pass neither,
                                            need no new entry in their list. */}
                                        {onReviewParse && isSavedCheckin && (
                                            <button
                                                onClick={() => {
                                                    onReviewParse(checkin.id)
                                                    setShowMenu(false)
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                                </svg>
                                                Show analysis
                                            </button>
                                        )}
                                        {availableActions.includes('discuss') && onDiscuss && (
                                            <button
                                                onClick={() => {
                                                    onDiscuss(checkin.id)
                                                    setShowMenu(false)
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                                </svg>
                                                Discuss
                                            </button>
                                        )}
                                        {isSavedCheckin && (
                                            <button
                                                onClick={() => {
                                                    setShowMenu(false)
                                                    setShowParseFeedback(true)
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                                </svg>
                                                Report
                                            </button>
                                        )}
                                        </div>
                                    )}
                                </div>
                            )}
                            </div>
                        )}
                        </div>
                        </div>
                    </div>

                    {renderBody
                        ? renderBody(checkin.body)
                        : <p className="text-gray-700 mb-3 whitespace-pre-wrap">{checkin.body}</p>}

                    {/* Projects, Features, Locations, People + map. Two-column
                        on md+: chips left, map right. Stays stacked under 768px
                        so the chips column has enough width to contain a wide
                        chip (`whitespace-nowrap`) without overflowing into the
                        map. Either column drops out when its data is missing —
                        chips alone go full-width via flex-1; map alone (rare:
                        needs coords without any chips, which today implies no
                        Locations feature row, impossible) would also expand. */}
                    {(() => {
                        const hasChips = (checkin.project_details && checkin.project_details.length > 0)
                            || hasLocationChips(checkin, features)
                            || hasPersonChips(checkin, collectionItems, features)
                        const hasMap = showMap && hasCheckinLocationsMap(checkin, collectionItems, features)
                        if (!hasChips && !hasMap) return null
                        return (
                          <div className="mt-2 flex flex-col md:flex-row md:items-start gap-3">
                            {hasChips && (
                              <div className="flex-1 min-w-0 space-y-2">
                            {checkin.project_details && checkin.project_details.length > 0 && (() => {

                                return checkin.project_details.filter(project => {
                                    // Only show projects that aren't marked with DELETE override
                                    const cp = checkin.checkin_projects.find(cp => cp.project_id === project.id)
                                    return !cp || cp.manual_override !== ManualOverrideType.DELETE
                                }).map(project => {
                                    // Find the corresponding checkin_project to check for manual_override
                                    const checkinProject = checkin.checkin_projects.find(cp => cp.project_id === project.id);
                                    const isProjectManuallyOverridden = checkinProject?.manual_override === ManualOverrideType.KEEP_VALUE;
                                    
                                    // Get features for this project. Visibility uses
                                    // `hasActiveCheckinFeatureRow` (any-non-DELETE) instead of a
                                    // single .find: list-typed features carry one row per value,
                                    // so a DELETE-tombstoned row sitting next to a keep_value row
                                    // would otherwise hide the whole feature.
                                    const projectFeatures = checkin.feature_details?.filter(feature => {
                                        return feature.project_id === project.id
                                            && hasActiveCheckinFeatureRow(checkin, feature.id);
                                    }) || [];
                                
                                return (
                                    <div key={project.id} className="flex flex-wrap items-center gap-2">
                                        {/* Project token */}
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0.6 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                            whileHover={{ y: -1 }}
                                            whileTap={{ scale: 0.96 }}
                                            style={{ transformOrigin: 'left center' }}
                                            className="inline-flex items-center rounded-full text-xs font-medium bg-plum-tint text-plum overflow-hidden whitespace-nowrap">
                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleFilterSelect('project', project);
                                                }}
                                                className="px-3 py-1 hover:bg-plum-tint-dark transition-colors cursor-pointer"
                                            >
                                                {project.emoji && (
                                                    <span className="mr-1">{project.emoji}</span>
                                                )}
                                                {project.title}
                                                {isProjectManuallyOverridden && (
                                                    <FeatureLockBadge title="Manually set" owner={checkinProject && ownerOf(checkinProject)} />
                                                )}
                                            </button>
                                            <div className="h-5 mx-1 border-l border-plum/30"></div>
                                            <Link
                                                to={`/projects/${project.id}`}
                                                className="pr-2 pl-1 hover:bg-plum-tint-dark transition-colors"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                </svg>
                                            </Link>
                                        </motion.div>

                                        {/* Features for this project */}
                                        {projectFeatures
                                            // Remove duplicates for list features
                                            .filter((feature, index, arr) => 
                                                arr.findIndex(f => f.id === feature.id) === index
                                            )
                                            .map(feature => {
                                        const checkinFeature = checkin.checkin_features.find(cf => cf.feature_id === feature.id);
                                        const featureType = feature.config?.type || 'decimal';
                                        
                                        // For list features, collect every active row — skipping
                                        // DELETE tombstones so a previously-removed value doesn't
                                        // leak into the chip text (e.g. friends showing "Laura,
                                        // Laura" when one of the two Lauras has been deleted).
                                        // `listRows` is the source for `ListValueDisplay` (chip
                                        // text); `allValues` is the raw-value projection used by
                                        // `ListValueCard` (the click-popover surface, scalar
                                        // subtypes only).
                                        const listRows = featureType === 'list' ? checkin.checkin_features
                                            .filter(cf => cf.feature_id === feature.id && cf.manual_override !== ManualOverrideType.DELETE)
                                            : [];
                                        const listType = feature.config?.params?.type;
                                        const allValues = featureType === 'list' ? listRows
                                            .map(cf => {
                                                if (listType === 'num') {
                                                    return cf.decimal_value;
                                                } else if (listType === 'bool') {
                                                    return boolLabelFor(cf.decimal_value, feature.config?.params);
                                                } else if (listType === 'reference') {
                                                    return cf.reference_value; // Keep as number for reference lists
                                                } else {
                                                    return cf.freeform_value;
                                                }
                                            })
                                            .filter(val => val !== undefined && val !== null) : [];

                                        // For map features, collect every active pair row (one row
                                        // per key→value). Same DELETE-tombstone filter as the list
                                        // case above; the per-pair override semantics covered in
                                        // Stage 4 of the plan will revisit this.
                                        const mapRows = featureType === 'map' ? checkin.checkin_features
                                            .filter(cf => cf.feature_id === feature.id && cf.manual_override !== ManualOverrideType.DELETE) : [];

                                        // Display value based on feature type
                                        let valueDisplay = "";
                                        if (featureType === 'list') {
                                            if (listRows.length > 0) {
                                                // `ListValueDisplay` owns the leading `: ` (same
                                                // contract as `MapValueDisplay`), so we just mark a
                                                // placeholder here and let the JSX swap in the
                                                // component.
                                                valueDisplay = 'LIST_PLACEHOLDER';
                                            }
                                        } else if (featureType === 'decimal') {
                                            const value = checkinFeature?.decimal_value;
                                            if (value !== undefined && value !== null) {
                                                valueDisplay = feature.unit ? `: ${value}${feature.unit}` : `: ${value}`;
                                            }
                                        } else if (featureType === 'bool') {
                                            // Read the live row: a DELETE tombstone keeps its 1/0
                                            // but is no longer an answer.
                                            const liveRow = checkin.checkin_features.find(cf =>
                                                cf.feature_id === feature.id && cf.manual_override !== ManualOverrideType.DELETE);
                                            const label = boolLabelFor(liveRow?.decimal_value, feature.config?.params);
                                            if (label !== null) {
                                                valueDisplay = `: ${label}`;
                                            }
                                        } else if (featureType === 'enum' || featureType === 'freeform') {
                                            // Both enum and freeform values are stored as freeform_value
                                            if (checkinFeature?.freeform_value) {
                                                valueDisplay = `: ${checkinFeature.freeform_value}`;
                                            }
                                        } else if (featureType === 'rating') {
                                            const value = checkinFeature?.decimal_value;
                                            if (value !== undefined && value !== null) {
                                                const max = feature.config?.params?.max || 5;
                                                valueDisplay = `: ${value}/${max}`;
                                            }
                                        } else if (featureType === 'labeled_rating') {
                                            const value = checkinFeature?.decimal_value;
                                            if (value !== undefined && value !== null) {
                                                const labels = feature.config?.params?.labels || [];
                                                const label = labels[Math.floor(value) - 1];
                                                if (label) {
                                                    valueDisplay = `: ${label}`;
                                                } else {
                                                    // Fallback to numeric display if label is missing
                                                    const max = feature.config?.params?.max || 5;
                                                    valueDisplay = `: ${value}/${max}`;
                                                }
                                            }
                                        } else if (featureType === 'reference') {
                                            // Reference values will be handled by ReferenceValueDisplay component
                                            // Set a placeholder that will be replaced by the component
                                            if (checkinFeature?.freeform_value) {
                                                valueDisplay = 'REFERENCE_PLACEHOLDER';
                                            }
                                        } else if (featureType === 'map') {
                                            if (mapRows.length > 0) {
                                                valueDisplay = 'MAP_PLACEHOLDER';
                                            }
                                        }

                                        const isReferenceFeature = featureType === 'reference' && checkinFeature?.reference_value;
                                        const collectionId = feature.config?.params?.collection_id;
                                        const peopleRefItem = (isReferenceFeature && collectionId)
                                            ? collectionItems[String(checkinFeature!.reference_value)]
                                            : null;
                                        const isPeopleSingleRef = peopleRefItem?.collection?.system_kind === 'people';

                                        const featureTokenContent = (
                                            <motion.div
                                                key={feature.id}
                                                initial={{ opacity: 0, scale: 0.6 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                                whileHover={{ y: -1 }}
                                                whileTap={{ scale: 0.96 }}
                                                style={{ transformOrigin: 'left center' }}
                                                className="inline-flex items-center rounded-full text-xs font-medium bg-amber-100 text-amber-900 overflow-hidden whitespace-nowrap min-w-0 max-w-full"
                                            >
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        if (isPeopleSingleRef) {
                                                            e.stopPropagation();
                                                            setPersonDetailValue({
                                                                collectionItemId: checkinFeature!.reference_value as number,
                                                                data: peopleRefItem!.data || {},
                                                            });
                                                            return;
                                                        }
                                                        // Reference / list / map all delegate to the wrapping
                                                        // ClickCard (popover) — don't fire a filter for them.
                                                        if (!isReferenceFeature && featureType !== 'list' && featureType !== 'map') {
                                                            handleFilterSelect('feature', feature);
                                                        }
                                                    }}
                                                    className="px-3 py-1 hover:bg-amber-200 transition-colors cursor-pointer min-w-0 overflow-hidden text-ellipsis"
                                                >
                                                    {feature.title}
                                                    {featureType === 'reference' && checkinFeature?.reference_value ? (
                                                        <ReferenceValueDisplay
                                                            itemId={checkinFeature.reference_value}
                                                            collectionItems={collectionItems}
                                                        />
                                                    ) : featureType === 'list' && valueDisplay === 'LIST_PLACEHOLDER' ? (
                                                        <ListValueDisplay
                                                            feature={feature}
                                                            rows={listRows}
                                                            collectionItems={collectionItems}
                                                        />
                                                    ) : featureType === 'map' && valueDisplay === 'MAP_PLACEHOLDER' ? (
                                                        <MapValueDisplay
                                                            feature={feature}
                                                            rows={mapRows}
                                                            collectionItems={collectionItems}
                                                        />
                                                    ) : (
                                                        valueDisplay !== 'REFERENCE_PLACEHOLDER' && valueDisplay !== 'LIST_PLACEHOLDER' && valueDisplay !== 'MAP_PLACEHOLDER' ? valueDisplay : ''
                                                    )}
                                                    {checkinFeature?.manual_override === ManualOverrideType.KEEP_VALUE && (
                                                        <FeatureLockBadge title="Manually set value" owner={ownerOf(checkinFeature)} />
                                                    )}
                                                </button>
                                                <div className="h-5 mx-1 border-l border-amber-300 shrink-0"></div>
                                                <Link
                                                    to={`/features/${feature.id}`}
                                                    className="pr-2 pl-1 hover:bg-amber-200 transition-colors shrink-0"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                    </svg>
                                                </Link>
                                            </motion.div>
                                        );

                                        // Single-ref into the People collection — the inner button's
                                        // onClick already opens PersonDetailModal, so no popover wrapper.
                                        if (isPeopleSingleRef) {
                                            return featureTokenContent;
                                        }

                                        if (isReferenceFeature && collectionId) {
                                            return (
                                                <ClickCard
                                                    key={feature.id}
                                                    content={
                                                        <CollectionItemHoverCard
                                                            collectionId={collectionId}
                                                            itemId={checkinFeature.reference_value}
                                                            itemName={feature.title}
                                                            onViewCheckins={() => handleFilterSelect('feature', feature)}
                                                        />
                                                    }
                                                >
                                                    {featureTokenContent}
                                                </ClickCard>
                                            );
                                        }

                                        // Handle list features with ClickCard
                                        if (featureType === 'list' && allValues.length > 0) {
                                            return (
                                                <ClickCard
                                                    key={feature.id}
                                                    content={
                                                        <ListValueCard
                                                            feature={feature}
                                                            values={allValues}
                                                            collectionItems={collectionItems}
                                                            onViewCheckins={() => handleFilterSelect('feature', feature)}
                                                            onRefresh={onRefresh}
                                                            onPersonClick={(v) => setPersonDetailValue(v)}
                                                        />
                                                    }
                                                >
                                                    {featureTokenContent}
                                                </ClickCard>
                                            );
                                        }

                                        // Map detail popover.
                                        if (featureType === 'map' && mapRows.length > 0) {
                                            return (
                                                <ClickCard
                                                    key={feature.id}
                                                    content={(close) => (
                                                        <MapValueCard
                                                            feature={feature}
                                                            rows={mapRows}
                                                            collectionItems={collectionItems}
                                                            onViewCheckins={() => {
                                                                close();
                                                                handleFilterSelect('feature', feature);
                                                            }}
                                                            onClose={close}
                                                            onPersonClick={(v) => setPersonDetailValue(v)}
                                                            onLocationClick={(v) => setLocationDetailValue(v)}
                                                            onGenericRefClick={(v) => setGenericRefValue(v)}
                                                        />
                                                    )}
                                                >
                                                    {featureTokenContent}
                                                </ClickCard>
                                            );
                                        }

                                        return featureTokenContent;
                                    })}
                                    </div>
                                );
                            });
                            })()}
                            <LocationChips
                                checkin={checkin}
                                collectionItems={collectionItems}
                                features={features}
                                highlightedKey={highlightedLocationKey}
                                onChipClick={(value) => setLocationDetailValue(value)}
                            />
                            <PersonChips checkin={checkin} collectionItems={collectionItems} features={features} onRefresh={onRefresh} />
                              </div>
                            )}
                            {hasMap && (
                              <div className={hasChips ? 'w-full md:w-64 md:flex-shrink-0' : 'flex-1'}>
                                <CheckinLocationsMap
                                    checkin={checkin}
                                    collectionItems={collectionItems}
                                    features={features}
                                    highlightedKey={highlightedLocationKey}
                                    onMarkerClick={(key) => setHighlightedLocationKey(key)}
                                    onExpand={() => setExpandedMapOpen(true)}
                                />
                              </div>
                            )}
                          </div>
                        )
                    })()}
                    <CheckinExpandedMapModal
                        open={expandedMapOpen}
                        checkin={checkin}
                        collectionItems={collectionItems}
                        features={features}
                        onClose={() => setExpandedMapOpen(false)}
                    />
                    <LocationDetailModal
                        open={locationDetailValue !== null}
                        value={locationDetailValue}
                        onEdit={(() => {
                            const id = locationDetailValue?.collectionItemId
                            if (id == null) return undefined
                            const item = collectionItems[String(id)]
                            const collectionId = item?.collection_id
                            if (collectionId == null) return undefined
                            // Google-backed Places aren't editable here — the
                            // Google Place is the source of truth (mirrors
                            // PlacesCollectionPage's Edit gate).
                            if (item?.data?.source === 'google') return undefined
                            return () => {
                                setLocationDetailValue(null)
                                setEditingCollectionItem({ collectionId, itemId: id })
                            }
                        })()}
                        onClose={() => setLocationDetailValue(null)}
                    />
                    <PersonDetailModal
                        open={personDetailValue !== null}
                        value={personDetailValue}
                        onEdit={(() => {
                            const id = personDetailValue?.collectionItemId
                            if (id == null) return undefined
                            const item = collectionItems[String(id)]
                            const collectionId = item?.collection_id
                            if (collectionId == null) return undefined
                            return () => {
                                setPersonDetailValue(null)
                                setEditingCollectionItem({ collectionId, itemId: id })
                            }
                        })()}
                        onClose={() => setPersonDetailValue(null)}
                    />
                    {genericRefValue && (
                        <CollectionItemModal
                            collectionId={genericRefValue.collectionId}
                            itemId={genericRefValue.itemId}
                            onClose={() => setGenericRefValue(null)}
                        />
                    )}
                    {editingCollectionItem && (
                        <EditCollectionItemById
                            collectionId={editingCollectionItem.collectionId}
                            itemId={editingCollectionItem.itemId}
                            onClose={() => setEditingCollectionItem(null)}
                            onSuccess={() => {
                                setEditingCollectionItem(null)
                                onRefresh?.()
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

export default CheckinItem